import { Box, Col, Link, Row, Text } from '@opendoor/bricks-next';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';

import { EntryComponent } from '../../cms/entries/entries';
import { IMetricBasic } from '../../declarations/contentful';

const formatMetric = (metricValue: number, metricFormat: IMetricBasic['fields']['format']) => {
  switch (metricFormat) {
    case 'currency':
      // just take whatever is inputted if it's less than 1000
      if (metricValue < 1000) {
        return `$${metricValue}`;
      }
      return `$${Math.round(metricValue / 1000).toFixed(0)}k`;
    case 'decimal':
      return metricValue.toLocaleString();
    case 'number':
      return metricValue.toLocaleString();
    case 'percent':
      return metricValue.toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 3 });
    default:
      return `${metricValue}`;
  }
};

const formatSource = (source?: string) => {
  if (!source) {
    return 'Opendoor';
  }
  try {
    const url = new URL(source);
    if (!url.protocol.startsWith('http')) {
      return source;
    }
    return (
      <Link
        typography="$bodySmall"
        href={source}
        analyticsName="cosmos-articles-basic-metric-source"
        aria-label=""
      >
        {url.host}
      </Link>
    );
  } catch {
    return source;
  }
};

const renderBasicMetric = (entry: IMetricBasic) => {
  const fields = entry.fields;
  if (fields.value === undefined) {
    return null;
  }

  let relativeChange: number | null = null;
  if (fields.comparisonValue) {
    const x1 = fields.comparisonValue;
    const x2 = fields.value;
    relativeChange = (x2 - x1) / x1;
  }
  return (
    <Box
      className="embedded-entry-wrapper"
      $platform-web={{ gridColumn: '5 / -2' }}
      key={fields.metricId || fields.metricName}
    >
      <Row alignItems="center" gap="$1">
        <Text
          tag="span"
          typography="$subheaderLarge"
          $largerThanMD={{ typography: '$headerMedium' }}
          color="$contentAccentBrand"
        >
          {formatMetric(fields.value, fields.format)}
        </Text>
        {relativeChange !== null && (
          <Col justifyContent="center" alignItems="center">
            {relativeChange < 0 ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />}
            <Text tag="span" typography="$bodyXsmall" $largerThanMD={{ typography: '$bodySmall' }}>
              {Math.round(relativeChange * 100).toFixed(0)}%
            </Text>
          </Col>
        )}
      </Row>
      <Text tag="span" typography="$bodyMedium">
        {fields.metricName}
      </Text>
      <Col gap="$1" mt="$2">
        <Text tag="span" typography="$bodySmall" color="$contentSecondary">
          Metric: {fields.metricDescription}
        </Text>
        {relativeChange !== null && (
          <Text tag="span" typography="$bodySmall" color="$contentSecondary">
            Comparison: {fields.comparisonDescription}
          </Text>
        )}
        <Text tag="span" typography="$bodySmall" color="$contentSecondary">
          Source: {formatSource(fields.metricSource)}
        </Text>
      </Col>
    </Box>
  );
};

const BasicMetric: EntryComponent<IMetricBasic> = {
  render: renderBasicMetric,
};

export default BasicMetric;
